import { z } from 'zod'

/**
 * Strict env-var validation. Missing or mistyped VITE_ values would
 * otherwise surface as cryptic runtime crashes deep inside Amplify or
 * Stripe — far away from the actual config bug.
 *
 * Validation is deliberately LAZY: import this module from the auth
 * provider or Stripe initialiser, NOT from the landing-page bundle, so
 * a developer browsing the marketing surfaces locally can still do so
 * with an empty .env. The first call to `env()` parses, caches, and
 * either returns the typed object or throws once with the full issue
 * list.
 */
export const EnvSchema = z.object({
  VITE_API_BASE_URL: z.string().url(),

  VITE_COGNITO_USER_POOL_ID: z.string().regex(/^[a-z]{2}-[a-z]+-\d_[A-Za-z0-9]+$/, {
    message: 'expected the form region_PoolID, e.g. eu-west-2_AbCdEf123',
  }),
  VITE_COGNITO_APP_CLIENT_ID: z.string().min(20),
  VITE_COGNITO_DOMAIN: z
    .string()
    .min(3)
    .refine((d) => !d.startsWith('http'), {
      message: 'VITE_COGNITO_DOMAIN must be a hostname without scheme',
    }),
  VITE_COGNITO_REGION: z.string().min(2),

  // `.optional()` only accepts `undefined`, never `""`. CI writes empty
  // strings into the .env file when a secret isn't set — wrap in
  // `z.preprocess` so an empty value coerces to `undefined` BEFORE the
  // inner schema runs, otherwise a missing Stripe/VAPID secret blows up
  // env validation for the whole site.
  VITE_STRIPE_PUBLISHABLE_KEY: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z
      .string()
      .startsWith('pk_', { message: 'expected a Stripe key (pk_test_… or pk_live_…)' })
      .optional(),
  ),

  /**
   * Base64URL-encoded VAPID public key — paired with the matching private
   * key configured inside the Firebase project (Cloud Messaging → Web
   * configuration). Firebase Messaging passes this to the browser's
   * subscribe call on our behalf; if missing the web-push subscribe button
   * stays disabled.
   */
  VITE_WEB_PUSH_VAPID_PUBLIC_KEY: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().min(20).optional(),
  ),

  // ── Firebase web config (FCM-for-web) ─────────────────────────────────
  // These six values come from the Firebase console: Project settings →
  // General → Your apps → Add web app. They identify the web app inside
  // the SAME Firebase project that already issues Android FCM tokens, so
  // backend delivery code (which uses the FCM Service Account secret) hits
  // both surfaces with the same payload. ALL public — Firebase web config
  // is designed to be shipped in the client bundle; security lives on the
  // server-side Service Account.
  VITE_FIREBASE_API_KEY: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().min(10).optional(),
  ),
  VITE_FIREBASE_AUTH_DOMAIN: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().min(3).optional(),
  ),
  VITE_FIREBASE_PROJECT_ID: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().min(3).optional(),
  ),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().regex(/^\d+$/, { message: 'numeric sender id from Firebase console' }).optional(),
  ),
  VITE_FIREBASE_APP_ID: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().min(10).optional(),
  ),
  /**
   * Firebase Analytics Measurement ID — the `G-…` string from Project
   * Settings → General → Your apps → SDK setup and configuration.
   * Optional (analytics falls back to silent no-op when missing) so a
   * fresh clone without Firebase env vars can still run the dev server.
   */
  VITE_FIREBASE_MEASUREMENT_ID: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().regex(/^G-/, { message: "Firebase measurement id starts with 'G-'" }).optional(),
  ),

  /**
   * Google Maps Platform API key — drives Places Autocomplete on the
   * meetup venue field AND the embedded venue map on event detail.
   * Restrict the key (HTTP referrer + APIs: Maps Embed API + Places API
   * (New) + Maps JavaScript API) in Cloud Console before shipping to
   * prod. Optional — venue input falls back to a plain text field and
   * the map block hides when the key is missing.
   */
  VITE_GOOGLE_MAPS_API_KEY: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().min(20).optional(),
  ),

  /**
   * Base URL of the Paxion forms API — host of the `/alpha/check-approval`
   * NDA gate that signup queries before allowing a Cognito account to be
   * created. Comes from terraform output `forms_api_url` in the
   * paxion_website repo. Leave empty to disable the NDA gate (dev
   * convenience only — production should always have this set).
   *
   * Alpha-tester admin (invite/list/resend/revoke) lives on the
   * internal staff dashboard at internal-dev.paxionfoundry.com — NOT
   * here. The e404 customer SPA never calls `/alpha/admin/*`.
   */
  VITE_PAXION_API_BASE: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().url().optional(),
  ),

  /**
   * Discord OAuth client id — needed for the profile "link Discord" flow.
   * The Discord OAuth app must list a `redirect_uri` matching
   * `${window.location.origin}/auth/discord-callback`. Leave blank to
   * disable Discord linking in this build (the button surfaces a
   * configure-me notice instead of crashing).
   */
  VITE_DISCORD_CLIENT_ID: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().min(10).optional(),
  ),

  VITE_APP_ENV: z.enum(['dev', 'prod']).default('dev'),

  /**
   * Gates public sign-in and sign-up. When false, `/login` and `/signup`
   * render the coming-soon page instead of the auth screens, and the nav
   * offers "Coming soon" in place of the two entry buttons.
   *
   * FAIL-CLOSED BY DESIGN: only the exact string "true" opens signups.
   * Everything else — absent, empty, "1", "yes" — leaves them shut.
   *
   * The direction is the whole point. CI writes an empty string for any
   * secret that isn't set (see VITE_STRIPE_PUBLISHABLE_KEY above), so a
   * variable deleted or mistyped in a GitHub environment arrives as ''. Were
   * the default "open", that mistake would quietly reopen public signups with
   * nothing failing and nothing to see. Closed is the recoverable direction.
   */
  VITE_SIGNUPS_OPEN: z.preprocess(coerceSignupsOpen, z.boolean()),
})

/**
 * Coercion for VITE_SIGNUPS_OPEN, shared by the schema above and by
 * `signupsOpen()` below so the two can never disagree about what "open" means.
 */
function coerceSignupsOpen(v: unknown): boolean {
  return String(v ?? '').trim().toLowerCase() === 'true'
}

/**
 * Whether public sign-in and sign-up are open, WITHOUT triggering full env
 * validation.
 *
 * This exists because validation here is deliberately lazy (see the module
 * comment): `env()` throws when any required VITE_ value is missing, so calling
 * it from the route tree would break `npm run dev` against an empty .env — the
 * exact scenario the laziness protects. The route gate needs this answer on
 * every page load, including the marketing surfaces, so it reads the one
 * variable directly.
 */
export function signupsOpen(): boolean {
  return coerceSignupsOpen(import.meta.env.VITE_SIGNUPS_OPEN)
}

export type Env = z.infer<typeof EnvSchema>

export interface EnvIssue {
  /** Dotted path of the failing field — e.g. `VITE_API_BASE_URL`. */
  field: string
  /** Zod's human-readable reason. */
  message: string
  /** The actual value that came through `import.meta.env` (trimmed +
   * length-revealing for empties; never the raw secret for already-validated
   * fields). Useful for spotting placeholder values like `"..."`. */
  receivedHint: string
}

let cached: Env | null = null
let cachedIssues: EnvIssue[] | null = null
let parsedOnce = false

function describeReceived(value: unknown): string {
  if (value === undefined) return '(unset)'
  if (value === null) return '(null)'
  if (typeof value === 'string') {
    if (value === '') return '(empty string)'
    if (value.length <= 8) return `"${value}"`
    return `"${value.slice(0, 4)}…${value.slice(-2)}" (${value.length} chars)`
  }
  return `(${typeof value})`
}

function parseEnvOnce() {
  if (parsedOnce) return
  parsedOnce = true
  const raw = {
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_COGNITO_USER_POOL_ID: import.meta.env.VITE_COGNITO_USER_POOL_ID,
    VITE_COGNITO_APP_CLIENT_ID: import.meta.env.VITE_COGNITO_APP_CLIENT_ID,
    VITE_COGNITO_DOMAIN: import.meta.env.VITE_COGNITO_DOMAIN,
    VITE_COGNITO_REGION: import.meta.env.VITE_COGNITO_REGION,
    VITE_STRIPE_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
    VITE_WEB_PUSH_VAPID_PUBLIC_KEY: import.meta.env.VITE_WEB_PUSH_VAPID_PUBLIC_KEY,
    VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
    VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
    VITE_FIREBASE_MEASUREMENT_ID: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    VITE_GOOGLE_MAPS_API_KEY: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    VITE_PAXION_API_BASE: import.meta.env.VITE_PAXION_API_BASE,
    VITE_DISCORD_CLIENT_ID: import.meta.env.VITE_DISCORD_CLIENT_ID,
    VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
    VITE_SIGNUPS_OPEN: import.meta.env.VITE_SIGNUPS_OPEN,
  } as const
  const parsed = EnvSchema.safeParse(raw)
  if (parsed.success) {
    cached = parsed.data
    cachedIssues = null
    return
  }
  cachedIssues = parsed.error.issues.map((i) => {
    const field = i.path.join('.') || '(root)'
    const received = describeReceived(
      (raw as Record<string, unknown>)[field as keyof typeof raw],
    )
    return { field, message: i.message, receivedHint: received }
  })
  // Always log to the console so a CI-deployed app surfaces the problem
  // even when `isEnvReady()` swallows the thrown error upstream. Without
  // this, "Auth isn't configured" renders with zero clue why.
  console.error(
    '[env] Validation failed — these fields are missing or invalid:\n' +
      cachedIssues
        .map(
          (issue) =>
            `  • ${issue.field}: ${issue.message} (received ${issue.receivedHint})`,
        )
        .join('\n'),
  )
}

/**
 * Returns the validated env object. Throws with the full issue list on
 * the first miss; subsequent calls return the cached parse.
 */
export function env(): Env {
  parseEnvOnce()
  if (cached) return cached
  const summary = (cachedIssues ?? [])
    .map((i) => `  • ${i.field}: ${i.message}`)
    .join('\n')
  throw new Error(
    `Missing or invalid environment variables.\n${summary}\n\nPopulate .env.local (local) or the dev Environment secrets (CI) from terraform output values.`,
  )
}

/** True when the env can be validated. Safe to call from non-auth surfaces. */
export function isEnvReady(): boolean {
  parseEnvOnce()
  return cached !== null
}

/**
 * Returns the validation issues from the last parse attempt, or `null`
 * if env was valid (or hasn't been read yet). Used by
 * `UnconfiguredNotice` to render the actual failing fields on-screen so
 * the user doesn't have to dig through console output.
 */
export function envIssues(): EnvIssue[] | null {
  parseEnvOnce()
  return cachedIssues
}
